import React, { useState, useEffect } from 'react';
import { Trash2, Plus, CheckSquare, Square, AlertCircle } from 'lucide-react';
import { ProductionModalProps, ProductionPeriodRow } from './ProductionModal.types';
import { useApp } from '../../context/AppContext';
import { UsersController } from '../../pages/settings/users.controller';
import { ProductsModel } from '../../pages/products/products.model';

function toIsoDate(dateStr: string): string {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const parts = dateStr.split('/');
  if (parts.length === 3) {
    const day = parts[0].padStart(2, '0');
    const month = parts[1].padStart(2, '0');
    let year = parts[2];
    if (year.length === 2) year = '20' + year;
    return `${year}-${month}-${day}`;
  }
  return dateStr;
}

export default function ProductionModal({
  isOpen,
  onClose,
  orderNumber,
  itemId,
  itemDescription,
  clientName = "Restaurante A",
  city = "Bauru",
  deliveryDate = "14/04/2026",
  weekday = "Segunda-feira",
  startedAtInfo,
  activeSector,
  initialPeriods,
  orderItems = [],
  onStart,
  onSave,
  onFinish
}: ProductionModalProps) {

  const { activeOperator } = useApp();

  // Dynamic user list from registered users configuration + fallback names
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);

  useEffect(() => {
    const users = UsersController.list();
    const names = users.map(u => u.fullName);
    const defaultNames = ["Gabriel", "Wel", "Leo", "Welington", "André", "Marcos"];
    if (activeOperator?.name && activeOperator.name !== 'Não Conectado') {
      defaultNames.unshift(activeOperator.name);
    }
    const merged = Array.from(new Set([...names, ...defaultNames]));
    setAvailableUsers(merged);
  }, [activeOperator]);

  // Helper function to map administrative login roles to initial production sector
  const getInitialSectorByRole = (role: string): string => {
    const sectors = ["Corte", "Dobra", "Solda", "Acabamento", "Acessórios", "Embalagem", "Coleta", "Instalação"];
    const normRole = (role || '').toLowerCase();
    const found = sectors.find(s => s.toLowerCase() === normRole || (normRole === 'logística' && s === 'Coleta'));
    return found || "Corte";
  };

  // Local state for selected sector (allows manual override as requested)
  const [selectedSector, setSelectedSector] = useState<string>(() => {
    return activeSector ? activeSector : getInitialSectorByRole(activeOperator?.role || 'Corte');
  });

  // Synchronize component when primitive activeSector prop changes
  useEffect(() => {
    if (activeSector) {
      setSelectedSector(activeSector);
    } else if (activeOperator?.role) {
      setSelectedSector(getInitialSectorByRole(activeOperator.role));
    }
  }, [activeSector, activeOperator]);

  const [occurrencesText, setOccurrencesText] = useState<string>("");

  // Dynamic dynamic period rows state: if initialPeriods is empty, load totally empty/zeroed out
  const [periods, setPeriods] = useState<ProductionPeriodRow[]>(() => {
    const raw = initialPeriods && initialPeriods.length > 0 ? initialPeriods : [];
    if (raw.length > 0) {
      return raw.map(p => ({ ...p, date: toIsoDate(p.date || '') }));
    }
    return [];
  });

  // Re-initialize periods: when no saved periods are found for this item, always start zeroed out (empty)
  useEffect(() => {
    if (isOpen) {
      const match = initialPeriods && initialPeriods.length > 0 
        ? initialPeriods.filter(p => (p.sector || "").toLowerCase() === selectedSector.toLowerCase())
        : [];
      
      if (match.length > 0) {
        setPeriods(match.map(p => ({ ...p, date: toIsoDate(p.date || '') })));
        
        // Load existing occurrences from saved periods of the current sector
        const savedPeriod = match.find(p => p.occurrences && p.occurrences.trim() !== "");
        setOccurrencesText(savedPeriod ? savedPeriod.occurrences || "" : "");
      } else {
        setPeriods([]);
        setOccurrencesText("");
      }
    }
  }, [isOpen, selectedSector, initialPeriods, orderNumber]);

  // Handler to update specified fields dynamically for controlled row inputs
  const handleUpdatePeriodField = (pId: string, field: keyof ProductionPeriodRow, val: string) => {
    setPeriods(prev => prev.map(p => p.id === pId ? { ...p, [field]: val } : p));
  };

  // Installer Selections (For Instalação Sector)
  const [installer1, setInstaller1] = useState<string>(() => {
    return activeOperator?.role?.toLowerCase() === 'instalação' ? activeOperator.name : '';
  });
  const [installer2, setInstaller2] = useState<string>("");
  const [installer3, setInstaller3] = useState<string>("");

  // Logistics Status select (For Coleta/Logística Sector)
  const [logisticsStatus, setLogisticsStatus] = useState<string>("Deixar na transportadora");

  // Helper to map real items of the selected order into Dobra Checklist
  const buildRealDobraChecklist = () => {
    const currentItemIdx = orderItems && orderItems.length > 0 && itemId 
      ? orderItems.findIndex((_, idx) => (idx + 1).toString().padStart(2, "0") === itemId) 
      : -1;
    const currentItem = currentItemIdx >= 0 ? orderItems[currentItemIdx] : null;

    const products = ProductsModel.getProducts();
    const activeProduct = currentItem 
      ? products.find(p => Number(p.id) === Number(currentItem.productId)) 
      : null;

    const list: { id: string; name: string; checked: boolean; isSub: boolean }[] = [];
    const idxStr = itemId || "01";
    const pName = currentItem?.productName || itemDescription || "Coifa";

    // Adiciona o item principal para pré-conferência
    list.push({
      id: `dobra-main-${currentItem?.id || 'temp'}`,
      name: `Item ${idxStr} - ${pName}`,
      checked: currentItem ? !!currentItem.isProduced : false,
      isSub: false
    });

    // Adiciona as partes/peças reais (se houver no cadastro do produto)
    if (activeProduct && Array.isArray(activeProduct.pieces)) {
      activeProduct.pieces.forEach((piece, index) => {
        if (piece && piece.trim() !== "") {
          list.push({
            id: `dobra-piece-${index}-${currentItem?.id || 'temp'}`,
            name: `   ↳ Parte: ${piece}`,
            checked: currentItem ? !!currentItem.isProduced : false,
            isSub: true
          });
        }
      });
    }

    // Adiciona os acessórios reais (se houver no cadastro do produto)
    if (activeProduct && Array.isArray(activeProduct.accessories)) {
      activeProduct.accessories.forEach((acc, index) => {
        if (acc && acc.trim() !== "") {
          list.push({
            id: `dobra-acc-${index}-${currentItem?.id || 'temp'}`,
            name: `   ↳ Acessório: ${acc}`,
            checked: currentItem ? !!currentItem.isProduced : false,
            isSub: true
          });
        }
      });
    }

    return list;
  };

  // Helper to map real items of the selected order into Embalagem Checklist
  const buildRealEmbalagemChecklist = () => {
    const currentItemIdx = orderItems && orderItems.length > 0 && itemId 
      ? orderItems.findIndex((_, idx) => (idx + 1).toString().padStart(2, "0") === itemId) 
      : -1;
    const currentItem = currentItemIdx >= 0 ? orderItems[currentItemIdx] : null;

    const products = ProductsModel.getProducts();
    const activeProduct = currentItem 
      ? products.find(p => Number(p.id) === Number(currentItem.productId)) 
      : null;

    const list: { id: string; name: string; checked: boolean; isSub: boolean }[] = [];
    const idxStr = itemId || "01";
    const pName = currentItem?.productName || itemDescription || "Coifa";

    // Adiciona o item principal para conferência final
    list.push({
      id: `emb-main-${currentItem?.id || 'temp'}`,
      name: `Item ${idxStr} - ${pName}`,
      checked: currentItem ? !!currentItem.isProduced : false,
      isSub: false
    });

    // Adiciona as partes reais para conferência final
    if (activeProduct && Array.isArray(activeProduct.pieces)) {
      activeProduct.pieces.forEach((piece, index) => {
        if (piece && piece.trim() !== "") {
          list.push({
            id: `emb-piece-${index}-${currentItem?.id || 'temp'}`,
            name: `   ↳ Parte: ${piece}`,
            checked: currentItem ? !!currentItem.isProduced : false,
            isSub: true
          });
        }
      });
    }

    // Adiciona os acessórios reais para conferência final
    if (activeProduct && Array.isArray(activeProduct.accessories)) {
      activeProduct.accessories.forEach((acc, index) => {
        if (acc && acc.trim() !== "") {
          list.push({
            id: `emb-acc-${index}-${currentItem?.id || 'temp'}`,
            name: `   ↳ Acessório: ${acc}`,
            checked: currentItem ? !!currentItem.isProduced : false,
            isSub: true
          });
        }
      });
    }

    return list;
  };

  // DOBRA Checklist State (loaded dynamically based on real orderItems)
  const [dobraChecklist, setDobraChecklist] = useState(() => buildRealDobraChecklist());

  // EMBALAGEM Checklist State (loaded dynamically based on real orderItems)
  const [embalagemChecklist, setEmbalagemChecklist] = useState(() => buildRealEmbalagemChecklist());

  // Sync checklist description when description props change on toggle
  useEffect(() => {
    if (isOpen) {
      setDobraChecklist(buildRealDobraChecklist());
      setEmbalagemChecklist(buildRealEmbalagemChecklist());
    }
  }, [isOpen, itemId, itemDescription, orderItems]);

  // DOBRA Checklist manipulation
  const handleToggleDobra = (id: string) => {
    setDobraChecklist(dobraChecklist.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // EMBALAGEM Checklist manipulation
  const handleToggleEmbalagem = (id: string) => {
    setEmbalagemChecklist(embalagemChecklist.map(item => 
      item.id === id ? { ...item, checked: !item.checked } : item
    ));
  };

  // Helper to append row
  const handleAddRow = () => {
    const newId = String(Date.now());
    
    // Obter o colaborador do login ativo, se disponível, ou fallback inteligente
    let defaultColab = activeOperator?.name && activeOperator.name !== 'Não Conectado' 
      ? activeOperator.name 
      : "Gabriel";

    const todayStr = new Date().toISOString().split('T')[0];
    const timeStr = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    setPeriods([
      ...periods,
      { id: newId, date: todayStr, sector: selectedSector, collaborator: defaultColab, start: timeStr, finish: '' }
    ]);
  };

  // Helper to remove row
  const handleRemoveRow = (id: string) => {
    if (periods.length > 1) {
      setPeriods(periods.filter(p => p.id !== id));
    }
  };

  // Validate Finish Eligibility
  const isDobraComplete = dobraChecklist.every(item => item.checked);
  const isEmbalagemComplete = embalagemChecklist.every(item => item.checked);
  const isInstallersComplete = !!(installer1 || installer2 || installer3);

  let isFinishDisabled = false;
  if (selectedSector.toLowerCase() === 'dobra') {
    isFinishDisabled = !isDobraComplete;
  } else if (selectedSector.toLowerCase() === 'embalagem') {
    isFinishDisabled = !isEmbalagemComplete;
  } else if (selectedSector.toLowerCase() === 'instalação') {
    isFinishDisabled = !isInstallersComplete;
  }

  // Handle Form Submission
  const handleAction = (actionType: 'START' | 'SAVE' | 'FINISH') => {
    const formEl = document.getElementById('production-form-native') as HTMLFormElement;
    if (formEl) {
      // Bloqueio de Ocorrência: Se houver texto digitado mas nenhum período com hora de início correspondente
      const hasLaunchedPeriod = periods.some(p => p.start && p.start.trim() !== "");
      if (occurrencesText.trim() !== "" && !hasLaunchedPeriod) {
        alert("Bloqueio de Ocorrência:\nNão é possível lançar uma ocorrência sem registrar pelo menos um período de produção válido (com horário de início) para este setor.");
        return;
      }

      // Bloqueio de Finalização: Não permite finalizar se houver período incompleto (sem hora final) ou se não houver períodos
      if (actionType === 'FINISH') {
        if (periods.length === 0) {
          alert("Bloqueio de Finalização:\nNão é possível finalizar esta etapa de produção sem registrar pelo menos um período de produção completo (com horários de início e fim) para este setor.");
          return;
        }

        const hasIncompletePeriod = periods.some(p => 
          !p.start || p.start.trim() === "" ||
          !p.finish || p.finish.trim() === "" || p.finish.trim() === "--:--"
        );

        if (hasIncompletePeriod) {
          alert("Bloqueio de Finalização:\nNão é possível finalizar esta etapa de produção sem definir o horário de término (FIM) para todos os períodos cadastrados.");
          return;
        }
      }

      const formData = new FormData(formEl);
      formData.append('action_type', actionType);
      formData.append('active_sector', selectedSector);
      formData.append('order_number', orderNumber);
      formData.append('item_id', itemId);
      formData.append('logistics_status', logisticsStatus);
      formData.append('installer_1', installer1);
      formData.append('installer_2', installer2);
      formData.append('installer_3', installer3);

      // Anexar a ocorrência a cada período do setor ativo que será enviado para o banco
      const periodsWithOccurrences = periods.map(p => ({
        ...p,
        occurrences: occurrencesText.trim()
      }));
      formData.append('periods_json', JSON.stringify(periodsWithOccurrences));

      if (actionType === 'START') {
        onStart(formData);
      } else if (actionType === 'SAVE') {
        onSave(formData);
      } else if (actionType === 'FINISH') {
        onFinish(formData);
      }
    }
  };

  // Encontrar o período mais antigo para obter a data/hora real de início no setor ativo
  const getStartedAtInfoText = (): string => {
    if (startedAtInfo) return startedAtInfo;
    const sectorPeriods = periods.filter(p => (p.sector || "").toLowerCase() === selectedSector.toLowerCase());
    if (sectorPeriods.length > 0) {
      const activePeriods = sectorPeriods.filter(p => p.start);
      if (activePeriods.length > 0) {
        const firstPeriod = activePeriods[0];
        let formattedDate = firstPeriod.date;
        if (firstPeriod.date && firstPeriod.date.includes('-')) {
          const dParts = firstPeriod.date.split('-');
          formattedDate = `${dParts[2]}/${dParts[1]}/${dParts[0]}`;
        }
        return `Produção iniciada em ${formattedDate} às ${firstPeriod.start}min`;
      }
    }
    return `Nenhum período de produção iniciado para o setor ${selectedSector}`;
  };

  // If modal is not active, return null (safe placement below all React hooks!)
  if (!isOpen) return null;

  // Decide Row 2 Format type
  // Dobra, Corte, Solda, Acabamento, Acessórios -> Nº Pedido, Item, Descrição
  // Embalagem, Coleta/Logística, Instalação -> Nº Pedido, Cliente, Cidade, Data, dia
  const isFormatB = ['embalagem', 'coleta', 'logística', 'instalação'].includes(selectedSector.toLowerCase());

  return (
    <div 
      className="modal fade show d-block animate-fade-in" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} 
      id="production-modal-window"
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div 
          className="modal-content shadow-lg border-0" 
          style={{ 
            borderRadius: '16px', 
            overflow: 'hidden', 
            backgroundColor: '#eaeaea',
            color: '#333333'
          }}
        >
          {/* HEADER ROW WITH INTEGRATED CLOSE BUTTON */}
          <div className="modal-header border-0 d-flex justify-content-between align-items-center bg-white py-3 px-4">
            <h4 className="modal-title font-weight-bold m-0 text-dark font-sans d-flex align-items-center gap-2">
              Controle de período de produção
            </h4>
            <button 
              type="button" 
              onClick={onClose} 
              className="btn btn-link link-dark text-decoration-none m-0 p-0 font-weight-bold"
              style={{ fontSize: '1.4rem', border: 'none' }}
              title="Fechar"
              id="close-button-modal"
            >
              X
            </button>
          </div>

          <form id="production-form-native" onSubmit={e => e.preventDefault()} className="m-0">
            <div className="modal-body px-4 py-3" style={{ maxHeight: '74vh', overflowY: 'auto' }}>
              
              {/* DATE INFO SUBHEADER */}
              <div className="text-center mb-3">
                <span className="text-muted text-xs font-weight-semibold">
                  {getStartedAtInfoText()}
                </span>
              </div>

              {/* DYNAMIC HEADER - FORMAT A (Corte/Dobra/Solda...) OR FORMAT B (Embalagem/Coleta/Instalacao) */}
              {!isFormatB ? (
                /* Format A Row */
                <div className="row g-2 mb-3 bg-white p-2 rounded shadow-sm border">
                  <div className="col-4 col-sm-3">
                    <label className="d-block text-muted text-center text-xs font-weight-bold text-uppercase mb-1">Nº Pedido</label>
                    <input 
                      type="text" 
                      className="form-control form-control-plaintext text-center font-mono font-weight-bold" 
                      style={{ fontSize: '1rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={orderNumber || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-4 col-sm-2">
                    <label className="d-block text-muted text-center text-xs font-weight-bold text-uppercase mb-1">Item</label>
                    <input 
                      type="text" 
                      className="form-control form-control-plaintext text-center font-mono font-weight-bold" 
                      style={{ fontSize: '1rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={itemId || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-12 col-sm-7 mt-2 mt-sm-0">
                    <label className="d-block text-muted text-start text-xs font-weight-bold text-uppercase mb-1">Descrição</label>
                    <input 
                      type="text" 
                      className="form-control form-control-plaintext font-weight-bold px-2" 
                      style={{ fontSize: '1rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={itemDescription || ''} 
                      readOnly 
                    />
                  </div>
                </div>
              ) : (
                /* Format B Row: Nº Pedido, Cliente, Cidade, Data, dia */
                <div className="row g-2 mb-3 bg-white p-2 rounded shadow-sm border">
                  <div className="col-4 col-md-2">
                    <label className="d-block text-muted text-center text-xs font-weight-bold text-uppercase mb-1">Nº Pedido</label>
                    <input 
                      type="text" 
                      className="form-control text-center font-mono font-weight-bold" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={orderNumber || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-8 col-md-3">
                    <label className="d-block text-muted text-start text-xs font-weight-bold text-uppercase mb-1">Cliente</label>
                    <input 
                      type="text" 
                      className="form-control font-weight-bold" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={clientName || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-6 col-md-3">
                    <label className="d-block text-muted text-start text-xs font-weight-bold text-uppercase mb-1">Cidade</label>
                    <input 
                      type="text" 
                      className="form-control font-weight-bold" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={city || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="d-block text-muted text-center text-xs font-weight-bold text-uppercase mb-1">Data</label>
                    <input 
                      type="text" 
                      className="form-control text-center font-weight-bold font-mono" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={deliveryDate || ''} 
                      readOnly 
                    />
                  </div>
                  <div className="col-12 col-md-2 mt-2 mt-md-0">
                    <label className="d-block text-muted text-center text-xs font-weight-bold text-uppercase mb-1">dia</label>
                    <input 
                      type="text" 
                      className="form-control text-center font-weight-bold" 
                      style={{ fontSize: '0.9rem', backgroundColor: '#fdfdfd', border: '1px solid #ced4da', borderRadius: '4px' }} 
                      value={weekday || ''} 
                      readOnly 
                    />
                  </div>
                </div>
              )}

              {/* MANUAL SECTOR SWITCH PANEL */}
              <div className="d-flex align-items-center justify-content-end gap-2 mb-3 bg-white p-2 rounded border shadow-sm">
                <span className="text-secondary text-xs font-weight-bold text-uppercase">Trocar Setor Ativo:</span>
                <select 
                  className="form-select form-select-sm border font-weight-bold bg-warning text-dark"
                  style={{ width: '150px', cursor: 'pointer' }}
                  value={selectedSector || ''}
                  onChange={(e) => setSelectedSector(e.target.value)}
                >
                  <option value="Corte">Corte</option>
                  <option value="Dobra">Dobra</option>
                  <option value="Solda">Solda</option>
                  <option value="Acabamento">Acabamento</option>
                  <option value="Acessórios">Acessórios</option>
                  <option value="Embalagem">Embalagem</option>
                  <option value="Coleta">Coleta/Transporte</option>
                  <option value="Instalação">Instalação</option>
                </select>
              </div>

              {/* DYNAMIC TRACK TIME PERIOD TABLE */}
              <div className="bg-white rounded border shadow-sm p-3 mb-3">
                <div className="table-responsive">
                  
                  {/* COLETAS / LOGÍSTICA TABLE FORMAT */}
                  {selectedSector.toLowerCase() === 'coleta' || selectedSector.toLowerCase() === 'logística' ? (
                    <table className="table table-borderless table-sm align-middle text-center mb-0">
                      <thead>
                        <tr className="text-muted font-weight-bold text-uppercase text-xs border-bottom">
                          <th>Dia</th>
                          <th>Setor</th>
                          <th>Colaborador</th>
                          <th>Status</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((period, idx) => (
                          <tr key={period.id}>
                            <td>
                              <input 
                                type="date" 
                                name={`log_date_${idx}`} 
                                value={period.date || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'date', e.target.value)}
                                className="form-control form-control-sm text-center font-mono font-weight-semibold" 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                name={`log_sect_${idx}`} 
                                value={period.sector || ''} 
                                readOnly
                                className="form-control form-control-sm text-center font-weight-bold bg-light" 
                              />
                            </td>
                            <td>
                              <select 
                                name={`log_colab_${idx}`}
                                value={period.collaborator || ''}
                                onChange={(e) => handleUpdatePeriodField(period.id, 'collaborator', e.target.value)}
                                className="form-select form-select-sm font-weight-bold"
                              >
                                {availableUsers.map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select 
                                className="form-select form-select-sm font-weight-semibold"
                                value={logisticsStatus || ''}
                                onChange={(e) => setLogisticsStatus(e.target.value)}
                              >
                                <option value="Coleta">Coleta</option>
                                <option value="entrega">entrega</option>
                                <option value="Deixar na transportadora">Deixar na transportadora</option>
                                <option value="aguardar retirada">aguardar retirada</option>
                              </select>
                            </td>
                            <td>
                              <button 
                                type="button" 
                                className="btn btn-sm text-danger" 
                                onClick={() => handleRemoveRow(period.id)}
                                disabled={periods.length <= 1}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : selectedSector.toLowerCase() === 'instalação' ? (
                    /* INSTALAÇÃO TABLE FORMAT: Dia | Setor | Colaborador 1 | Colaborador 1 | Colaborador 1 */
                    <table className="table table-borderless table-sm align-middle text-center mb-0">
                      <thead>
                        <tr className="text-muted font-weight-bold text-uppercase text-xs border-bottom">
                          <th>Dia</th>
                          <th>Setor</th>
                          <th>Colaborador 1</th>
                          <th>Colaborador 2</th>
                          <th>Colaborador 3</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((period, idx) => (
                          <tr key={period.id}>
                            <td>
                              <input 
                                type="date" 
                                name={`ins_date_${idx}`} 
                                value={period.date || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'date', e.target.value)}
                                className="form-control form-control-sm text-center font-mono font-weight-semibold" 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                name={`ins_sect_${idx}`} 
                                value={period.sector || ''} 
                                readOnly
                                className="form-control form-control-sm text-center font-weight-bold bg-light" 
                              />
                            </td>
                            <td>
                              <select 
                                name={`ins_colab1_${idx}`}
                                className="form-select form-select-sm font-weight-semibold"
                                value={installer1 || ''}
                                onChange={(e) => setInstaller1(e.target.value)}
                              >
                                <option value="">-- Selecione --</option>
                                {availableUsers.map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select 
                                name={`ins_colab2_${idx}`}
                                className="form-select form-select-sm font-weight-semibold"
                                value={installer2 || ''}
                                onChange={(e) => setInstaller2(e.target.value)}
                              >
                                <option value="">-- Selecione --</option>
                                {availableUsers.map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <select 
                                name={`ins_colab3_${idx}`}
                                className="form-select form-select-sm font-weight-semibold"
                                value={installer3 || ''}
                                onChange={(e) => setInstaller3(e.target.value)}
                              >
                                <option value="">-- Selecione --</option>
                                {availableUsers.map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <button 
                                type="button" 
                                className="btn btn-sm text-danger" 
                                onClick={() => handleRemoveRow(period.id)}
                                disabled={periods.length <= 1}
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    /* STANDARD PRODUCTION TIME FORMAT (Corte, Dobra, Solda, Acabamento, Acessórios, Embalagem) */
                    <table className="table table-borderless table-sm align-middle text-center mb-0">
                      <thead>
                        <tr className="text-muted font-weight-bold text-uppercase text-xs border-bottom">
                          <th style={{ width: '130px' }}>Dia</th>
                          <th style={{ width: '120px' }}>Setor</th>
                          <th>Colaborador</th>
                          <th style={{ width: '100px' }}>início</th>
                          <th style={{ width: '100px' }}>fim</th>
                          <th style={{ width: '40px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((period, index) => (
                          <tr key={period.id}>
                            <td>
                              <input 
                                type="date" 
                                name={`p_date_${index}`} 
                                value={period.date || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'date', e.target.value)}
                                required
                                className="form-control form-control-sm text-center font-mono font-weight-bold text-dark" 
                              />
                            </td>
                            <td>
                              <input 
                                type="text" 
                                name={`p_sector_${index}`} 
                                value={period.sector || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'sector', e.target.value)}
                                required
                                className="form-control form-control-sm text-center font-weight-bold bg-light" 
                              />
                            </td>
                            <td>
                              <select 
                                name={`p_colab_${index}`} 
                                value={period.collaborator || ''}
                                onChange={(e) => handleUpdatePeriodField(period.id, 'collaborator', e.target.value)}
                                className="form-select form-select-sm font-weight-semibold"
                              >
                                {availableUsers.map(name => (
                                  <option key={name} value={name}>{name}</option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input 
                                type="time" 
                                name={`p_start_${index}`} 
                                value={period.start || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'start', e.target.value)}
                                required
                                className="form-control form-control-sm text-center font-mono font-weight-bold text-dark" 
                              />
                            </td>
                            <td>
                              <input 
                                type="time" 
                                name={`p_finish_${index}`} 
                                value={period.finish || ''} 
                                onChange={(e) => handleUpdatePeriodField(period.id, 'finish', e.target.value)}
                                className="form-control form-control-sm text-center font-mono font-weight-bold text-dark" 
                              />
                            </td>
                            <td className="text-center">
                              <button 
                                type="button" 
                                className="btn btn-link text-danger p-1"
                                onClick={() => handleRemoveRow(period.id)}
                                disabled={periods.length <= 1}
                                title="Remover período"
                              >
                                <Trash2 size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                </div>
              </div>

              {/* DOBRA STRUCTURAL PRE-CONFERENCE CHECKLIST */}
              {selectedSector.toLowerCase() === 'dobra' && (
                <div className="bg-white rounded border shadow-sm p-3 mb-3">
                  <h6 className="font-weight-bold text-uppercase text-secondary text-xs mb-3">
                    Pré-conferência dos itens (Exclui categoria acessórios)
                  </h6>
                  <p className="text-muted text-xs mb-3 font-weight-normal">
                    Todos os itens abaixo precisam ser checados e confirmados para habilitar a finalização do estágio de produção desse setor.
                  </p>
                  
                  <div className="d-flex flex-column gap-2" style={{ paddingLeft: '8px' }}>
                    {dobraChecklist.map((item) => (
                      <div 
                        key={item.id}
                        className={`d-flex align-items-center justify-content-between p-2 rounded cursor-pointer border ${item.checked ? 'bg-light border-success' : 'bg-white'}`}
                        onClick={() => handleToggleDobra(item.id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {item.checked ? (
                            <CheckSquare size={18} className="text-success fill-success" />
                          ) : (
                            <Square size={18} className="text-secondary" />
                          )}
                          <span 
                            className={`text-sm ${item.isSub ? 'font-mono text-muted ps-3' : 'font-weight-bold text-dark'}`}
                            style={{ whiteSpace: 'pre' }}
                          >
                            {item.name}
                          </span>
                        </div>
                        {item.checked && (
                          <span className="badge bg-success text-white text-xs py-1">OK</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {!isDobraComplete && (
                    <div className="alert alert-warning py-2 px-3 mt-3 m-0 text-xs d-flex align-items-center gap-2">
                      <AlertCircle size={15} />
                      <span>O botão <strong>FINALIZAR</strong> ficará bloqueado até que todos os itens da pré-conferência estejam checados.</span>
                    </div>
                  )}
                </div>
              )}

              {/* EMBALAGEM EXCLUSIVE EXPEDITION CHECKLISTS */}
              {selectedSector.toLowerCase() === 'embalagem' && (
                <div className="bg-white rounded border shadow-sm p-3 mb-3">
                  <h6 className="font-weight-bold text-uppercase text-secondary text-xs mb-3">
                    Conferência Final de Itens
                  </h6>
                  
                  {/* MAIN ITEMS */}
                  <div className="d-flex flex-column gap-1">
                    {embalagemChecklist.map((item) => (
                      <div 
                        key={item.id}
                        className={`d-flex align-items-center justify-content-between p-2 rounded cursor-pointer border ${item.checked ? 'bg-light border-primary' : 'bg-white'}`}
                        onClick={() => handleToggleEmbalagem(item.id)}
                      >
                        <div className="d-flex align-items-center gap-2">
                          {item.checked ? (
                            <CheckSquare size={18} className="text-primary fill-primary" />
                          ) : (
                            <Square size={18} className="text-secondary" />
                          )}
                          <span 
                            className={`text-sm ${item.isSub ? 'font-mono text-muted ps-3' : 'font-weight-bold text-dark'}`}
                            style={{ whiteSpace: 'pre' }}
                          >
                            {item.name}
                          </span>
                        </div>
                        {item.checked && (
                          <span className="badge bg-primary text-white text-xs py-1">OK</span>
                        )}
                      </div>
                    ))}
                  </div>

                  {!isEmbalagemComplete && (
                    <div className="alert alert-warning py-2 px-3 mt-3 m-0 text-xs d-flex align-items-center gap-2">
                      <AlertCircle size={15} />
                      <span>O botão <strong>FINALIZAR</strong> ficará inativo até que todos os itens da conferência final estejam checados.</span>
                    </div>
                  )}
                </div>
              )}

              {/* INSTALAÇÃO OPERATOR AT LEAST ONE VALIDATION BOX */}
              {selectedSector.toLowerCase() === 'instalação' && !isInstallersComplete && (
                <div className="alert alert-warning py-2 px-3 mb-3 text-xs d-flex align-items-center gap-2">
                  <AlertCircle size={15} />
                  <span>Preenchimento obrigatório de pelo menos um instalador para salvar ou finalizar.</span>
                </div>
              )}

              {/* GENERAL OCCURRENCES FIELD */}
              <div className="bg-white p-3 rounded border shadow-sm">
                <label className="form-label font-weight-bold text-uppercase text-secondary text-xs mb-2 d-flex justify-content-between align-items-center">
                  <span>Ocorrências</span>
                  <span className={`text-xs ${occurrencesText.length >= 150 ? 'text-danger fw-bold' : 'text-muted'}`}>
                    {150 - occurrencesText.length} caracteres restantes
                  </span>
                </label>
                <textarea 
                  name="modal_occurrencies" 
                  rows={2} 
                  className="form-control text-dark mb-0 text-sm" 
                  placeholder="Descreva quaisquer ocorrências extraordinárias, paradas de turno ou observações técnicas..."
                  style={{ borderRadius: '8px' }}
                  value={occurrencesText}
                  onChange={(e) => setOccurrencesText(e.target.value.substring(0, 150))}
                  maxLength={150}
                />
              </div>

            </div>

            {/* ACTION FOOTER BUTTONS */}
            <div className="modal-footer border-0 d-flex justify-content-center gap-3 bg-white py-3">
              <button 
                type="button" 
                onClick={handleAddRow}
                className="btn btn-primary text-white font-weight-bold text-uppercase px-4 py-2"
                style={{ borderRadius: '4px', minWidth: '130px', border: 'none', backgroundColor: '#007bff' }}
              >
                Iniciar
              </button>
              
              <button 
                type="button" 
                onClick={() => handleAction('SAVE')}
                disabled={selectedSector.toLowerCase() === 'instalação' && !isInstallersComplete}
                className="btn btn-success text-white font-weight-bold text-uppercase px-4 py-2"
                style={{ borderRadius: '4px', minWidth: '130px', border: 'none', backgroundColor: '#28a745' }}
              >
                Salvar
              </button>
              
              <button 
                type="button" 
                onClick={() => handleAction('FINISH')}
                disabled={isFinishDisabled}
                className="btn btn-danger text-white font-weight-bold text-uppercase px-4 py-2"
                style={{ borderRadius: '4px', minWidth: '130px', border: 'none', backgroundColor: '#dc3545' }}
              >
                Finalizar
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
}
